import { Controller } from '@hotwired/stimulus';

/**
 * ValueChangeWarningController
 *
 * A general-purpose Stimulus controller that displays warning modals when form field values
 * are changed from specific trigger values.
 *
 * @example Basic Usage
 * <div data-controller="value-change-warning"
 *      data-value-change-warning-modal-id-value="warningModal"
 *      data-value-change-warning-warning-fields-value='[{"field": "status", "triggerValue": "live"}]'>
 *
 *   <select data-value-change-warning-target="field"
 *           data-field-name="status"
 *           data-action="change->value-change-warning#checkForWarning">
 *     <option value="live">Live</option>
 *     <option value="inactive">Inactive</option>
 *   </select>
 * </div>
 *
 * @example Multiple Fields
 * data-value-change-warning-warning-fields-value='[
 *   {"field": "status", "triggerValue": "live"},
 *   {"field": "groupType", "triggerValue": "active"}
 * ]'
 *
 * Values:
 * - modalId: ID of the modal to show when warning is triggered
 * - warningFields: Array of objects defining which field changes should trigger warnings
 *   - field: matches the data-field-name attribute on the input
 *   - triggerValue: the initial value that, when changed from, triggers the warning
 *
 * Targets:
 * - field: Form inputs to monitor for changes (must have data-field-name attribute)
 *
 * Actions:
 * - checkForWarning: Called on field change events to evaluate if warning should be shown
 */
export default class extends Controller {
  static targets = ['field'];

  static values = {
    modalId: String,
    warningFields: Array
  };

  connect() {
    this.initialValues = new Map();
    this.warningsShown = new Set();

    this.fieldTargets.forEach((field) => {
      this.initialValues.set(field.dataset.fieldName, field.value);
    });
  }

  checkForWarning() {
    this.fieldTargets.forEach((field) => {
      const fieldName = field.dataset.fieldName;
      const warningConfig = this.warningFieldsValue.find((config) => config.field === fieldName);

      if (!warningConfig) return;

      const initialValue = this.initialValues.get(fieldName);
      const currentValue = field.value;
      const warningKey = `${fieldName}_${warningConfig.triggerValue}`;

      const shouldShowWarning = initialValue === warningConfig.triggerValue
        && currentValue !== warningConfig.triggerValue
        && !this.warningsShown.has(warningKey);

      if (shouldShowWarning) {
        this.showWarning();
        this.warningsShown.add(warningKey);
      }
    });
  }

  showWarning() {
    $(`#${this.modalIdValue}`).modal('show');
  }
}
